import {fetchClient, setAuthToken, type components} from '../../../../shared/api'
import {AuthModel, UserModel} from './_models'

// `photo` (SCRUM-580) is newer than the codegen schema; so are PATCH me/ and the
// tokens in the change-password answer — see wms-own-profile-scrum-580.md.
type WMSUser = components['schemas']['WMSUser'] & {photo?: string | null}

const client = fetchClient as unknown as {
  PATCH: (path: string, opts: {body: unknown}) => Promise<{data?: unknown; error?: unknown}>
  POST: (path: string, opts: {body: unknown}) => Promise<{data?: unknown; error?: unknown}>
}

// `permissions` is documented as `string` in swagger but the endpoint returns
// a string[] (e.g. ["wms:page:5.0:view", ...]). Normalise both shapes.
const normalizePermissions = (raw: WMSUser['permissions']): string[] => {
  if (Array.isArray(raw)) {
    return raw as string[]
  }
  if (typeof raw === 'string' && raw.trim()) {
    return raw.split(/[\s,]+/).filter(Boolean)
  }
  return []
}

// Map the backend WMS user (`/wms/auth/me/`) onto the Metronic UserModel the
// layout already consumes (first_name, last_name, email, ...).
const wmsUserToModel = (u: WMSUser): UserModel => ({
  id: u.id ?? 0,
  username: u.phone_number ?? '',
  password: undefined,
  email: u.email ?? '',
  first_name: u.first_name ?? '',
  last_name: u.last_name ?? '',
  fullname: [u.first_name, u.last_name].filter(Boolean).join(' '),
  phone: u.phone_number,
  occupation: u.wms_role ?? undefined,
  pic: u.photo ?? undefined,
  permissions: normalizePermissions(u.permissions),
})

// POST /wms/auth/login/ → { access, refresh } (JWT)
export async function login(phone_number: string, password: string): Promise<{data: AuthModel}> {
  const {data, error} = await fetchClient.POST('/wms/auth/login/', {
    body: {phone_number, password},
  })

  // The swagger types the 201 body as WMSLogin (echoes the request), but the
  // endpoint actually returns JWT tokens — read them through AuthModel.
  const auth = data as unknown as AuthModel | undefined
  if (error || !auth?.access) {
    throw new Error('Invalid credentials')
  }

  setAuthToken(auth.access)
  return {data: auth}
}

// POST /wms/auth/logout/ — invalidates the session on the backend.
// Errors are swallowed: the local session is cleared regardless.
export async function logout(): Promise<void> {
  try {
    await fetchClient.POST('/wms/auth/logout/')
  } catch (error) {
    console.error(error)
  }
}

// GET /wms/auth/me/ — the Bearer token is attached by the fetchClient middleware.
export async function getUserByToken(_token?: string): Promise<{data: UserModel}> {
  const {data, error} = await fetchClient.GET('/wms/auth/me/')
  if (error || !data) {
    throw new Error('Failed to load current user')
  }

  return {data: wmsUserToModel(data)}
}

// PATCH /wms/auth/me/ — JSON, or multipart when a photo is uploaded. The 200 is
// the same body as GET me/. Throws the backend's `{errors: [...]}` body.
export async function updateMe(body: FormData | Record<string, unknown>): Promise<UserModel> {
  const {data, error} = await client.PATCH('/wms/auth/me/', {body})
  if (error || !data) {
    throw error ?? new Error('Failed to update the profile')
  }

  return wmsUserToModel(data as WMSUser)
}

// POST /wms/auth/change-password/ — the change revokes this tab's refresh token,
// so the caller must store the new pair. Throws the backend's `{errors: [...]}` body.
export async function changePassword(
  old_password: string,
  new_password: string
): Promise<Partial<AuthModel> & {detail?: string}> {
  const {data, error} = await client.POST('/wms/auth/change-password/', {
    body: {old_password, new_password},
  })
  if (error || !data) {
    throw error ?? new Error('Failed to change the password')
  }

  return data as Partial<AuthModel> & {detail?: string}
}

export function register(
  _email: string,
  _firstname: string,
  _lastname: string,
  _password: string,
  _password_confirmation: string
): Promise<{data: AuthModel}> {
  return Promise.reject(new Error('Registration is not implemented yet'))
}

export function requestPassword(_email: string): Promise<{data: {result: boolean}}> {
  return Promise.reject(new Error('Password reset is not implemented yet'))
}
